import {Form} from '@/plugins/FWForms/index';
import axios from 'axios';
import * as types from '@/store/mutation-types';
import dayjs from 'dayjs';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import type {
    AddonContributionConfig,
    Subscription,
    SubscriptionConfigPayload,
    SubscriptionPlan,
    SubscriptionState
} from '@/types/subscription';
import {d} from '@/utils/consoleHelper.ts';
import {push} from 'notivue';
import {handleApiError} from '@/utils/errorHandler.ts';

const normalizeAddonSubscriptions = (payload: unknown): Subscription[] => {
    if (Array.isArray(payload)) {
        return payload as Subscription[];
    }

    if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: Subscription[] }).data)) {
        return (payload as { data: Subscription[] }).data as Subscription[];
    }

    return [];
};

const normalizeAddonContributionMap = (payload: unknown): Record<string, AddonContributionConfig> => {
    if (!payload || typeof payload !== 'object') {
        return {};
    }

    return Object.entries(payload as Record<string, Partial<AddonContributionConfig>>).reduce((acc, [price, config]) => {
        if (!price) {
            return acc;
        }

        const perUnit = typeof config?.per_unit === 'number' ? config.per_unit : 0;

        if (perUnit <= 0) {
            return acc;
        }

        acc[price] = {
            per_unit: perUnit,
            default_quantity: typeof config?.default_quantity === 'number' ? config.default_quantity : 0,
        };

        return acc;
    }, {} as Record<string, AddonContributionConfig>);
};

const normalizeFeatureCounts = (payload: unknown) => {
    if (!payload || typeof payload !== 'object') {
        return {};
    }

    return Object.entries(payload as Record<string, Record<string, number>>).reduce((acc, [feature, values]) => {
        if (!values || typeof values !== 'object') {
            return acc;
        }

        acc[feature] = Object.entries(values).reduce((featureAcc, [plan, value]) => {
            if (typeof value === 'number') {
                featureAcc[plan] = value;
            }

            return featureAcc;
        }, {} as Record<string, number>);

        return acc;
    }, {} as Record<string, Record<string, number>>);
};

const normalizePlanFeatures = (payload: unknown) => {
    if (!payload || typeof payload !== 'object') {
        return {};
    }

    return Object.entries(payload as Record<string, Record<string, boolean>>).reduce((acc, [plan, features]) => {
        if (!features || typeof features !== 'object') {
            return acc;
        }

        acc[plan] = Object.entries(features).reduce((featureAcc, [feature, value]) => {
            featureAcc[feature] = Boolean(value);

            return featureAcc;
        }, {} as Record<string, boolean>);

        return acc;
    }, {} as Record<string, Record<string, boolean>>);
};

const normalizeLegacyPrices = (legacy: unknown): { monthly: string[], annual: string[] } => {
    const source = (legacy && typeof legacy === 'object') ? legacy as Record<string, unknown> : {};
    const toStringArray = (value: unknown): string[] =>
        Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];

    return {
        monthly: toStringArray(source.monthly),
        annual: toStringArray(source.annual),
    };
};

const normalizePriceMap = (payload: unknown) => {
    if (!payload || typeof payload !== 'object') {
        return {};
    }

    return Object.entries(payload as Record<string, {
        monthly?: string | null,
        annual?: string | null,
        legacy?: unknown,
    }>).reduce((acc, [plan, prices]) => {
        if (!prices || typeof prices !== 'object') {
            return acc;
        }

        acc[plan] = {
            monthly: prices.monthly || null,
            annual: prices.annual || null,
            legacy: normalizeLegacyPrices(prices.legacy),
        };

        return acc;
    }, {} as Record<string, {
        monthly: string | null,
        annual: string | null,
        legacy: { monthly: string[], annual: string[] }
    }>);
};

const normalizeAddonPrices = (payload: unknown) => {
    if (!payload || typeof payload !== 'object') {
        return {};
    }

    return Object.entries(payload as Record<string, {
        monthly?: string | null
    }>).reduce((acc, [addon, prices]) => {
        if (!prices || typeof prices !== 'object') {
            return acc;
        }

        acc[addon] = {
            monthly: prices.monthly || null,
        };

        return acc;
    }, {} as Record<string, { monthly: string | null }>);
};

type PlanPrices = {
    monthly?: string | null,
    annual?: string | null,
    legacy?: {
        monthly?: string[],
        annual?: string[],
    },
};

const resolvePlanFromPriceMap = (priceId: string | null, priceMap: Record<string, PlanPrices>): string | null => {
    if (!priceId) return null;

    for (const [plan, prices] of Object.entries(priceMap)) {
        if (!prices) continue;

        if (prices.monthly === priceId || prices.annual === priceId) {
            return plan;
        }

        if (prices.legacy?.monthly?.includes(priceId) || prices.legacy?.annual?.includes(priceId)) {
            return plan;
        }
    }

    return null;
};

const resolveDurationFromPriceMap = (priceId: string | null, priceMap: Record<string, PlanPrices>): string => {
    if (!priceId) return 'monthly';

    for (const [, prices] of Object.entries(priceMap)) {
        if (prices?.annual === priceId || prices?.legacy?.annual?.includes(priceId)) {
            return 'yearly';
        }

        if (prices?.monthly === priceId || prices?.legacy?.monthly?.includes(priceId)) {
            return 'monthly';
        }
    }

    return 'monthly';
};


// state
const state: SubscriptionState = {
    loading: {
        subscription: false,
        subscribing: false,
        cancelSubscription: false,
        resumeSubscription: false,
        endTrial: false,
    },

    enforceSubscription: false,

    newSubscription: {
        billingPeriod: null,
        plan: null,
    },

    subscriptionForm: new Form(),

    plans: [
        {
            name: 'basic',
            pricing: {
                monthly: {
                    id: 'sub_basic_monthly',
                    price: 34,
                },
                yearly: {
                    id: 'sub_basic_annual',
                    price: 24,
                },
            },
            description: {
                bullets: [
                    'Orders',
                    'Up to 5 vehicles',
                    'Includes 1 branch',
                    '2 staff accounts',
                    'Unlimited Customers',
                    '1 car-share connections',
                    'Online Store',
                    'Stripe Integration',
                    'And More*',
                ],
            },
        },
        {
            name: 'standard',
            pricing: {
                monthly: {
                    id: 'sub_standard_monthly',
                    price: 89,
                },
                yearly: {
                    id: 'sub_standard_annual',
                    price: 69,
                },
            },
            description: {
                bullets: [
                    'All of Basic',
                    'Up to 15 vehicles',
                    '5 staff accounts',
                    'Includes 2 branches',
                    'eSign Rental Agreements',
                    'Coupons',
                    'QR & Barcodes',
                    'Reports',
                    'Import/Export',
                    'Hide powered by Fleetwire',
                    'And More*',
                ],
            },
        },
        {
            name: 'premium',
            pricing: {
                monthly: {
                    id: 'sub_premium_monthly',
                    price: 279,
                },
                yearly: {
                    id: 'sub_premium_annual',
                    price: 239,
                },
            },
            description: {
                bullets: [
                    'All of Standard',
                    'Up to 150 vehicles',
                    '20 staff accounts',
                    'Includes 2 branches',
                    'Custom Domain',
                    'Custom Email Domain',
                    'Additional Scripts',
                    '10 Car-share connections',
                    'Zapier integration',
                    'API',
                    'TFA (Two factor authentication)',
                    'And More*',
                ],
            },
        },
    ] as SubscriptionPlan[],

    subscription: {
        stripe_status: null,
        ends_at: null,
        items: [],
        name: null,
        quantity: null,
        stripe_id: null,
        stripe_price: null,
        trial_ends_at: null,
        updated_at: null,
    },

    subscriptionMaster: null,
    addonSubscriptions: [] as Subscription[],
    addonContributionMap: {} as Record<string, AddonContributionConfig>,
    planFeatureCounts: {},
    planFeatureFlags: {},
    planPriceMap: {},
    addonPrices: {},
    extraListingsAddonCount: 0,

    subscriptionStep: 0,
    planSelection: 0,

    updatedSubscription: {
        plan: 'basic',
        interval: 'monthly',
    }
};

const getters = {
    loading: (state: SubscriptionState) => state.loading,
    enforceSubscription: (state: SubscriptionState) => state.enforceSubscription,
    plans: (state: SubscriptionState) => state.plans,
    planFeatureCounts: (state: SubscriptionState) => state.planFeatureCounts,
    planFeatureFlags: (state: SubscriptionState) => state.planFeatureFlags,
    planPriceMap: (state: SubscriptionState) => state.planPriceMap,
    addonPrices: (state: SubscriptionState) => state.addonPrices,
    subscription: (state: SubscriptionState) => state.subscription,
    addonSubscriptions: (state: SubscriptionState) => state.addonSubscriptions,
    newSubscription: (state: SubscriptionState) => state.newSubscription,
    subscriptionForm: (state: SubscriptionState) => state.subscriptionForm,
    subscriptionMaster: (state: SubscriptionState) => state.subscriptionMaster,
    extraListingsAddonCount: (state: SubscriptionState) => state.extraListingsAddonCount,
    addonContributionMap: (state: SubscriptionState) => state.addonContributionMap,
    subscriptionPlan: (state: SubscriptionState) => {
        const planFromPriceMap = resolvePlanFromPriceMap(state.subscriptionMaster, state.planPriceMap);

        if (planFromPriceMap) {
            return planFromPriceMap;
        }

        return null;
    },
    subscriptionDuration: (state: SubscriptionState) => {
        return resolveDurationFromPriceMap(state.subscriptionMaster, state.planPriceMap);
    },
    hasActiveSubscription: (state: SubscriptionState) => state.subscription?.stripe_status === 'active',
    hasPastDueSubscription: (state: SubscriptionState) => ['past_due', 'unpaid'].includes(state.subscription?.stripe_status ?? ''),
    hasPausedSubscription: (state: SubscriptionState) => state.subscription?.stripe_status === 'paused',
    subscriptionEndDate: (state: SubscriptionState) => {
        const subEndDate = state.subscription?.ends_at;
        if (!subEndDate) return false;
        return dayjs(subEndDate).format('MMM D, YYYY');
    },
    subscriptionStep: (state: SubscriptionState) => state.subscriptionStep,
    planSelection: (state: SubscriptionState) => state.planSelection,
    updatedSubscription: (state: SubscriptionState) => state.updatedSubscription,
};

const mutations = {
    [types.SET_SUBSCRIPTION_LOADING](state: SubscriptionState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_ENFORCE_SUBSCRIPTION](state: SubscriptionState, enforce: boolean) {
        state.enforceSubscription = enforce;
    },
    [types.SET_SUBSCRIPTION_STEP](state: SubscriptionState, step: number) {
        state.subscriptionStep = step;
    },
    [types.SET_SUBSCRIPTION_PLAN_AND_INTERVAL](state: SubscriptionState, updatedSubscription: {
        plan: string,
        interval: string
    }) {
        state.updatedSubscription = updatedSubscription;
    },
    [types.SET_PLAN_SELECTION](state: SubscriptionState, selection: number) {
        state.planSelection = selection;
    },
    [types.SET_SUBSCRIPTION](state: SubscriptionState, subscription: Subscription) {
        state.subscription = subscription;
    },

    [types.SET_ADDON_SUBSCRIPTIONS](state: SubscriptionState, subscriptions: Subscription[]) {
        state.addonSubscriptions = Array.isArray(subscriptions) ? subscriptions : [];
    },

    [types.SET_ADDON_CONTRIBUTION_MAP](state: SubscriptionState, map: unknown) {
        state.addonContributionMap = normalizeAddonContributionMap(map);
    },
    [types.SET_SUBSCRIPTION_CONFIG](state: SubscriptionState, payload: SubscriptionConfigPayload) {
        state.planFeatureCounts = normalizeFeatureCounts(payload?.featureCounts);
        state.planFeatureFlags = normalizePlanFeatures(payload?.planFeatures);
        state.planPriceMap = normalizePriceMap(payload?.priceMap);
        state.addonPrices = normalizeAddonPrices(payload?.addonPrices);
    },

    /**
     * Update the new subscription object
     */
    [types.UPDATE_NEW_SUBSCRIPTION](state: SubscriptionState, newSubscription) {
        if (newSubscription.billingPeriod) {
            state.newSubscription.billingPeriod = newSubscription.billingPeriod;
        }
        if (newSubscription.plan) {
            state.newSubscription.plan = newSubscription.plan;
        }
    },
    [types.SET_SUBSCRIPTION_MASTER](state: SubscriptionState, subscription: string | null = null) {
        d.dir('SET_SUBSCRIPTION_MASTER');
        d.dir(subscription);
        state.subscriptionMaster = subscription;
    },
    [types.SET_EXTRA_LISTINGS_ADDON](state: SubscriptionState, count: number) {
        state.extraListingsAddonCount = count;
    },
    [types.CLEAR_SUBSCRIPTION_FORM_ERRORS](state: SubscriptionState, form_field: string | null = null) {
        if (form_field === null || form_field === undefined) {
            state.subscriptionForm.errors.clear();
        } else {
            state.subscriptionForm.errors.clear(form_field);
        }
    },
    [types.SET_SUBSCRIPTION_FORM_ERROR](state: SubscriptionState, error: string | Record<string, string[]>) {
        state.subscriptionForm.errors.set(error);
    },
};

const actions = {

    async subscribeToPlan({commit}, sub) {
        try {
            const responseData = await performApiRequest(commit, 'SET_SUBSCRIPTION_LOADING', 'subscribing', () => axios.post('/v1/subscription', {id: sub}));
            commit(types.SET_SUBSCRIPTION, responseData.subscription);
            commit('company/SET_COMPANY', responseData.company, {root: true});

            push.success({
                message: 'subscription successful',
            });
        } catch (error) {
            handleApiError(error);
        }
    },

    async switchPlan({commit}, sub: string) {
        try {
            const responseData = await performApiRequest(commit, 'SET_SUBSCRIPTION_LOADING', 'subscribing', () => axios.post('/v1/subscription/swap', {id: sub}));
            push.success({
                message: 'Subscription swap successful',
            });
            return responseData;
        } catch (error) {
            handleApiError(error, 'There was an error swapping your subscription.');
        }
    },

    async addAddon({commit}, data: { id: string, count: number }) {
        try {
            const responseData = await performApiRequest(commit, 'SET_SUBSCRIPTION_LOADING', 'subscribing', () => axios.post('/v1/subscription/addon', data));

            commit('SET_SUBSCRIPTION', responseData.subscription);
            commit('SET_ADDON_SUBSCRIPTIONS', normalizeAddonSubscriptions(responseData.addonSubscriptions));
            if (typeof responseData.extraListingsAddon === 'number') {
                commit('SET_EXTRA_LISTINGS_ADDON', responseData.extraListingsAddon);
            }
            push.success({
                message: 'subscription add-on added',
            });

            return responseData;
        } catch (error) {
            handleApiError(error);
        }
    },

    async removeAddon({commit}, payload: { id: string, count?: number } | string) {
        const requestData = typeof payload === 'string'
            ? {id: payload, count: 1}
            : {id: payload.id, count: payload?.count ?? 1};


        try {
            const responseData = await performApiRequest(commit, 'SET_SUBSCRIPTION_LOADING', 'subscribing', () => axios.delete(`/v1/subscription/addon/${requestData.id}`, {
                data: {count: requestData.count},
            }));
            commit(types.SET_SUBSCRIPTION, responseData.subscription);
            commit(types.SET_ADDON_SUBSCRIPTIONS, normalizeAddonSubscriptions(responseData.addonSubscriptions));
            if (typeof responseData.extraListingsAddon === 'number') {
                commit('SET_EXTRA_LISTINGS_ADDON', responseData.extraListingsAddon);
            }
            push.success({
                message: 'subscription add-on removed',
            });

            return responseData;
        } catch (error) {
            handleApiError(error);
        }
    },

    cancelSubscription({commit}) {
        commit('SET_SUBSCRIPTION_LOADING', {subject: 'cancelSubscription', loading: true});
        commit('CLEAR_SUBSCRIPTION_FORM_ERRORS');

        axios.delete('/v1/subscription')
            .then((response) => {
                commit('SET_SUBSCRIPTION', response.data.subscription);
                if (response.data.addonSubscriptions) {
                    commit('SET_ADDON_SUBSCRIPTIONS', normalizeAddonSubscriptions(response.data.addonSubscriptions));
                }
                if (typeof response.data.extraListingsAddon === 'number') {
                    commit('SET_EXTRA_LISTINGS_ADDON', response.data.extraListingsAddon);
                }
                push.success({
                    message: 'Cancel subscription successful',
                });
            }).catch((e) => {
            commit(types.SET_SUBSCRIPTION_FORM_ERROR, e?.response?.data?.message || 'There was an error cancelling your subscription.');
        }).finally(() => {
            commit('SET_SUBSCRIPTION_LOADING', {subject: 'cancelSubscription', loading: false});
        });
    },

    async cancelSubscriptionStripePortalUrl({commit}) {
        return await performApiRequest(commit, 'SET_SUBSCRIPTION_LOADING', 'cancelSubscription', () => axios.get('/v1/subscription/cancel-portal'));
    },

    async resumeSubscription({commit}) {
        try {
            const responseData = await performApiRequest(commit, 'SET_SUBSCRIPTION_LOADING', 'resumeSubscription', () => axios.put('/v1/subscription'));
            commit('SET_SUBSCRIPTION', responseData.subscription);
            push.success({
                message: 'Resume subscription successful',
            });
            return responseData;
        } catch (error) {
            handleApiError(error, 'There was an error resuming your subscription.');
        }
    },

    /**
     * End the trial early and start a subscription
     */
    async endTrial({commit}) {

        try {
            const responseData = await performApiRequest(commit, 'SET_SUBSCRIPTION_LOADING', 'endTrial', () => axios.post('/v1/end-trial'));
            push.success({
                message: 'Ended trial successful',
            });
            return responseData;
        } catch (error) {
            handleApiError(error, 'There was an error ending your trial.');
            throw error;
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
